/**
 * Extension points: every place the interface can be added to.
 *
 * The core's own screens and menus are registered here exactly the way a
 * plugin registers its own, which is what keeps the two equal: there is no
 * private door for built-ins. A plugin (see plugins.ts) adds views to the
 * sidebar, items to any menu, actions to every track's ⋯ menu, pages to
 * Settings, whole layouts, and items to the status bar -- and when it is
 * disabled, everything it added is removed in one call.
 *
 * Components read the registry through `useRegistry()`, which re-renders them
 * when anything is added or removed.
 */

import { useMemo, useSyncExternalStore, type ReactNode } from "react";
import type { Track } from "../types";
import type { IconName } from "../shell/Icons";
import { getSession, may, useSession } from "./session";

export type MenuId =
  | "file" | "edit" | "view" | "library" | "playback" | "tools" | "plugins" | "help"
  | "track" | "flow" | "playlist" | "album" | "artist";

export interface MenuItem {
  id: string;
  label: string;
  icon?: IconName;
  shortcut?: string;
  hint?: string;
  run?: () => void | Promise<void>;
  disabled?: boolean;
  checked?: boolean;
  danger?: boolean;
  submenu?: MenuItem[];
  /** With accounts on, shown only to a role with this permission (core/session.ts). */
  permission?: string;
  /** Items are grouped and a separator drawn between groups. */
  group?: string;
  /** Lower comes first within a group. */
  order?: number;
}

export interface ViewContext {
  /** Open another view by id. */
  navigate: (viewId: string, params?: Record<string, string>) => void;
  params: Record<string, string>;
}

export interface ViewContribution {
  id: string;
  label: string;
  icon: IconName;
  section: "library" | "discover" | "plugins" | "hidden";
  order?: number;
  /** Shown beside the label in the sidebar (a count, "mix"…). */
  badge?: () => string | null;
  /** True when the screen has no use for the song details panel, so it stays shut. */
  ownPanel?: boolean;
  render: (ctx: ViewContext) => ReactNode;
  /** With accounts on, shown only to a role with this permission (core/session.ts). */
  permission?: string;
  /** Who added it ("core", or a plugin's id): a plugin's screens sit together under its name. Set by the registry. */
  owner?: string;
}

export interface TrackAction {
  id: string;
  label: string;
  icon?: IconName;
  group: string;
  order?: number;
  shortcut?: string;
  danger?: boolean;
  /** Hide the action when it does not apply. */
  when?: (tracks: Track[]) => boolean;
  /** With accounts on, shown only to a role with this permission. */
  permission?: string;
  run: (tracks: Track[]) => void | Promise<void>;
}

export interface SettingsPage {
  id: string;
  label: string;
  order?: number;
  render: () => ReactNode;
  /** With accounts on, shown only to a role with this permission. */
  permission?: string;
}

/** A person, as Users & roles lists them (what a user tool is given). */
export interface UserSummary {
  id: number;
  username: string;
  displayName: string | null;
}

/**
 * Something a plugin adds to each person in Users & roles (Radio: when they may go
 * live). A button on their row, labelled by `label(user)`; pressing it opens `render`
 * under the row (`close` hides it again).
 */
export interface UserTool {
  id: string;
  order?: number;
  label: (user: UserSummary) => ReactNode;
  title?: string;
  render: (user: UserSummary, close: () => void) => ReactNode;
  /** Shown only to a role with this permission (Users & roles already needs users.manage). */
  permission?: string;
}

export interface LayoutContribution {
  id: string;
  label: string;
  description: string;
  order?: number;
  render: () => ReactNode;
}

export interface StatusItem {
  id: string;
  order?: number;
  side?: "left" | "right";
  render: () => ReactNode;
}

interface Owned<T> {
  owner: string;
  item: T;
}

interface Snapshot {
  version: number;
  views: ViewContribution[];
  /** Plugins' names by id, for headings. */
  ownerNames: Record<string, string>;
  menus: Record<string, MenuItem[]>;
  trackActions: TrackAction[];
  settingsPages: SettingsPage[];
  layouts: LayoutContribution[];
  statusItems: StatusItem[];
  userTools: UserTool[];
}

const byOrder = <T extends { order?: number; label?: string; id: string }>(a: T, b: T) =>
  (a.order ?? 100) - (b.order ?? 100) || (a.label ?? a.id).localeCompare(b.label ?? b.id);

class Registry {
  private views: Owned<ViewContribution>[] = [];
  private menus: Owned<MenuItem & { menu: MenuId }>[] = [];
  private actions: Owned<TrackAction>[] = [];
  private pages: Owned<SettingsPage>[] = [];
  private layouts: Owned<LayoutContribution>[] = [];
  private status: Owned<StatusItem>[] = [];
  private tools: Owned<UserTool>[] = [];
  private listeners = new Set<() => void>();
  private names: Record<string, string> = {};
  private snapshot: Snapshot = this.build(0);

  private build(version: number): Snapshot {
    const menus: Record<string, MenuItem[]> = {};
    for (const { item } of this.menus) (menus[item.menu] ??= []).push(item);
    for (const key of Object.keys(menus)) menus[key].sort((a, b) => (a.group ?? "").localeCompare(b.group ?? "") || byOrder(a, b));
    return {
      version,
      views: this.views.map((o) => ({ ...o.item, owner: o.owner })).sort(byOrder),
      ownerNames: { ...this.names },
      menus,
      trackActions: this.actions.map((o) => o.item).sort((a, b) => a.group.localeCompare(b.group) || byOrder(a, b)),
      settingsPages: this.pages.map((o) => o.item).sort(byOrder),
      layouts: this.layouts.map((o) => o.item).sort(byOrder),
      statusItems: this.status.map((o) => o.item).sort(byOrder),
      userTools: this.tools.map((o) => o.item).sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id)),
    };
  }

  private changed() {
    this.snapshot = this.build(this.snapshot.version + 1);
    for (const fn of this.listeners) fn();
  }

  private add<T extends { id: string }>(list: Owned<T>[], item: T, owner: string): () => void {
    const existing = list.findIndex((o) => o.item.id === item.id);
    if (existing >= 0) {
      if (list[existing].owner !== owner) {
        throw new Error(`"${item.id}" is already registered by ${list[existing].owner}`);
      }
      list.splice(existing, 1);
    }
    const entry = { owner, item };
    list.push(entry);
    this.changed();
    return () => {
      const i = list.indexOf(entry);
      if (i >= 0) {
        list.splice(i, 1);
        this.changed();
      }
    };
  }

  addView(view: ViewContribution, owner = "core") {
    return this.add(this.views, view, owner);
  }
  addMenuItem(menu: MenuId, item: MenuItem, owner = "core") {
    return this.add(this.menus, { ...item, id: `${menu}:${item.id}`, menu }, owner);
  }
  addTrackAction(action: TrackAction, owner = "core") {
    return this.add(this.actions, action, owner);
  }
  addSettingsPage(page: SettingsPage, owner = "core") {
    return this.add(this.pages, page, owner);
  }
  addLayout(layout: LayoutContribution, owner = "core") {
    return this.add(this.layouts, layout, owner);
  }
  addStatusItem(item: StatusItem, owner = "core") {
    return this.add(this.status, item, owner);
  }
  addUserTool(tool: UserTool, owner = "core") {
    return this.add(this.tools, tool, owner);
  }

  /** A plugin's name, for the heading its screens sit under. */
  setOwnerName(owner: string, name: string) {
    if (this.names[owner] === name) return;
    this.names[owner] = name;
    this.changed();
  }

  /** The layouts one plugin added (the first-run setup switches to it). */
  layoutsOf(owner: string): LayoutContribution[] {
    return this.layouts.filter((o) => o.owner === owner).map((o) => o.item);
  }

  /** Remove everything one plugin added. */
  removeOwner(owner: string) {
    for (const list of [this.views, this.menus, this.actions, this.pages, this.layouts, this.status, this.tools] as Owned<{ id: string }>[][]) {
      for (let i = list.length - 1; i >= 0; i--) if (list[i].owner === owner) list.splice(i, 1);
    }
    this.changed();
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  get = () => this.snapshot;
}

export const registry = new Registry();

export function useRegistry(): Snapshot {
  const snap = useSyncExternalStore(registry.subscribe, registry.get, registry.get);
  const session = useSession();
  // With accounts on, only the screens and actions the signed-in role reaches.
  return useMemo(() => {
    if (!session.enabled) return snap;
    const ok = (p?: string) => !p || may(p, session);
    return { ...snap, views: snap.views.filter((v) => ok(v.permission)),
             trackActions: snap.trackActions.filter((a) => ok(a.permission)),
             settingsPages: snap.settingsPages.filter((p) => ok(p.permission)),
             userTools: snap.userTools.filter((t) => ok(t.permission)) };
  }, [snap, session]);
}

/** A menu without what the signed-in role cannot do (submenus too); unchanged with accounts off. */
export function allowedItems(items: MenuItem[], session = getSession()): MenuItem[] {
  if (!session.enabled) return items;
  return items
    .filter((i) => !i.permission || may(i.permission, session))
    .map((i) => (i.submenu ? { ...i, submenu: allowedItems(i.submenu, session) } : i));
}

/** Group a sorted list into runs, for drawing separators between groups. */
export function grouped<T extends { group?: string }>(items: T[]): T[][] {
  const out: T[][] = [];
  for (const item of items) {
    const last = out[out.length - 1];
    if (last && last[0].group === item.group) last.push(item);
    else out.push([item]);
  }
  return out;
}
